import { useEffect } from "react";
import * as Sentry from "@sentry/react";
import {
  keepSentryLog,
  scrubSentry,
  scrubSentryBreadcrumb,
  sentryDataCollection,
  setSentryUserContext,
  resolveSentryEnvironment,
  traceSampleRate,
} from "@heylife/core";

const dsn = import.meta.env.VITE_SENTRY_DSN || undefined;
const environment = resolveSentryEnvironment(
  import.meta.env.VITE_APP_ENV,
  import.meta.env.PROD ? "production" : "development",
);

function adminSentryScope() {
  const scope = Sentry.getCurrentScope();
  return {
    setUser: (user: { id: string } | null) => scope.setUser(user),
    setTag: (key: string, value: string | undefined) => scope.setTag(key, value),
    setContext: (name: string, context: Record<string, unknown> | null) => scope.setContext(name, context),
  };
}

/** Attach opaque user id and church tenant; clears on logout when userId is omitted. */
export function bindSentryUserContext(userId: string | null | undefined, churchId: string | null | undefined): void {
  if (!dsn) return;
  setSentryUserContext(adminSentryScope(), userId, churchId);
}

export function useSentryUserContext(userId: string | undefined, churchId: string | null | undefined): void {
  useEffect(() => {
    bindSentryUserContext(userId ?? null, churchId ?? null);
  }, [userId, churchId]);
}

Sentry.init({
  dsn,
  enabled: Boolean(dsn),
  environment,
  release: `heylife-admin@${import.meta.env.VITE_APP_VERSION ?? "dev"}`,
  dataCollection: sentryDataCollection(),
  integrations: [Sentry.browserTracingIntegration(), Sentry.consoleLoggingIntegration({ levels: ["warn", "error"] })],
  tracesSampleRate: traceSampleRate(environment),
  tracePropagationTargets: ["localhost", /^https:\/\/api\./],
  beforeSend(event) {
    return scrubSentry(event);
  },
  beforeSendLog(log) {
    if (!keepSentryLog(log.level)) return null;
    return scrubSentry(log);
  },
  beforeBreadcrumb(breadcrumb) {
    return scrubSentryBreadcrumb(breadcrumb);
  },
});
